import React from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';

import A from 'wanderu-component-react-router-link';
import { RECOMMENDED } from 'wanderu-ducks-search';
import {
    localize,
    selectLocale,
} from 'wanderu-i18n';
import {
    buildSearchUrlPath,
    changeDay,
} from 'wanderu-utils';

import { dayButtonClicked } from './DayButtonEvents';
import { useSortTypeSelector } from '../../../../hooks';

import styles from './DayButton.scss';

import type {
    DayButtonProps,
    SearchParams,
} from '../../../../types';

const adjustReturnDate = ({
    currentReturnDate,
    newDepartDate,
    originalDepartDate,
}: {
    currentReturnDate: string;
    newDepartDate: Date;
    originalDepartDate: string;
}): string => {
    const originalDate = new Date(originalDepartDate);
    // compare the original depart date with the new date the user clicked
    const diff = newDepartDate.getUTCDate() - originalDate.getUTCDate();

    // set returnDate to a date as far in the future as the new date is from the original search depart date
    return changeDay(currentReturnDate, diff);
};

const getSearchUrl = ({
    date,
    searchParams,
}: {
    date: string;
    searchParams: SearchParams;
}) => {
    const formattedDate = new Date(date);
    const queryString = window?.location?.search;
    const queries = Object.fromEntries(new URLSearchParams(queryString).entries());
    let returnDate = searchParams.returnDate;

    if (returnDate && (formattedDate?.getUTCDate() > new Date(returnDate)?.getUTCDate())) {
        // if the return date is in the past, adjust it to be as far in the future as the new date is from the original search depart date
        returnDate = adjustReturnDate({
            currentReturnDate: returnDate,
            newDepartDate: formattedDate,
            originalDepartDate: searchParams.departDate,
        });
    }

    const url = buildSearchUrlPath({
        arrivePlace: searchParams.destinationText,
        arrivePlaceId: searchParams.destinationPlaceId,
        departDate: date,
        departPlace: searchParams.originText,
        departPlaceId: searchParams.originPlaceId,
        returnDate,
    }, queries);

    return url;
};

function DayButton({
    date,
    i18nMoney,
    isActive,
    isDisabled,
    price,
    searchParams,
}: DayButtonProps) {
    const activeSortType = useSortTypeSelector(searchParams) || RECOMMENDED;
    const locale = useSelector(selectLocale);

    const [lang, loc] = locale.split('-');
    const formattedLocale = [lang, loc.toUpperCase()].join('-');

    const formattedDate = new Date(date);
    const dateString = new Intl.DateTimeFormat(formattedLocale, {
        day: 'numeric',
        timeZone: 'UTC',
        weekday: 'short',
    }).format(formattedDate);

    const priceString = price
        ? i18nMoney(price).format({
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        })
        : undefined;

    const handleClick = () => {
        dayButtonClicked({
            activeSortType,
            date,
            price,
            searchParams,
        });
    };

    return (
        <A
            className={classNames(
                styles.container,
                {
                    [styles.active]: isActive,
                    [styles.disabled]: isDisabled,
                },
            )} href={getSearchUrl({
                date,
                searchParams,
            })}
            onClick={handleClick}
            underline="none"
        >
            <span className={styles.date}>{dateString}</span>
            {priceString && <span className={styles.details}>{priceString}</span>}
        </A>
    );
}

export default localize(DayButton, ['i18nMoney'], 'baseApp');
